import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Badge,
  Button,
  CopyButton,
  Group,
  List,
  Loader,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { formatPrice } from "@livecommerce/ui";
import { ApiError } from "../api/client";
import { useMayRunShows } from "../auth/roles";
import { Broadcast } from "../broadcast/Broadcast";
import { Monitor } from "../monitor/Monitor";
import { ChatPanel } from "../chat/ChatPanel";
import { useRoomChat } from "../chat/useRoomChat";
import { PresenterOverlay } from "../presenter/PresenterOverlay";
import { GiveawayCard } from "./GiveawayCard";
import { ProductPicker } from "./ProductPicker";
import { Settlement } from "./Settlement";
import { ShowDetails } from "./ShowDetails";
import { ShowRedirect } from "./ShowRedirect";
import { ShowTestRun } from "./ShowTestRun";
import { ShowThumbnail } from "./ShowThumbnail";
import { LotRow } from "./LotRow";
import { panelPollInterval } from "./poll";
import { ShowStateBadge } from "./ShowsPage";
import {
  type Lot,
  type LotProblem,
  type Preflight,
  type ShowDetail,
  shareLink,
  shows,
} from "./api";
import styles from "./Shows.module.scss";

/**
 * One show: build it, publish it, run it, end it (spec §7, §8, §17.2, §17.4).
 *
 * ## Everything here re-reads the whole show
 *
 * Every action answers with what changed and the page throws that away and calls `read` again.
 * That is not laziness — it is the only correct choice while the panel has no real-time
 * transport. Two people run a show (`R-HOST-31`), so the local copy is stale the moment anyone
 * else acts, and patching it from an action's response would produce a screen that disagrees with
 * the database in ways nobody can see. Re-reading is one request against a show with at most a
 * few hundred lots. It is the same `GET` the room uses as the *snapshot* half of
 * snapshot-then-subscribe (arch §6.3), but the panel does not subscribe: the show's channel
 * withholds the quantities the panel exists to show, so it re-reads on a timer instead — see
 * `poll.ts`, and *Carried forward* in `docs/implementation-plan.md`.
 *
 * ## What is deliberately not here
 *
 * The real-time running order, and those of §17.4's at-a-glance figures the show read does not
 * carry — proximity to viewer capacity among them.
 *
 * Chat is one component — `ChatPanel` — which owns the room connection and the moderation
 * controls. What this page keeps is the same thing it keeps about video: the *decision* about when
 * to offer it, which is a show that is live, or ended for its mute list alone.
 *
 * Video is one component — `Broadcast` — which owns the camera, the microphone and the publish
 * connection. What this page keeps is the *decision*: the pre-flight check it
 * reports (`R-HOST-11`) is what enables or disables *go live*, so the control and the reason it is
 * disabled are not in two places arguing.
 */
export function ShowPage() {
  const { t } = useTranslation();
  const { showId } = useParams();
  /**
   * Whether this page offers a show or merely displays one (spec §3).
   *
   * A moderator gets the show, its running order read-only, and the comments card — which is
   * what they are for. Everything that changes the show is withheld, and withheld rather than
   * disabled: a row of greyed-out buttons is an invitation to ask why, and the answer is not
   * about this show. The routes behind every one of them answer 403 regardless
   * (`requireHost`), so this is the panel agreeing with the server rather than enforcing
   * anything.
   *
   * The pre-flight check comes with `Broadcast`, so withholding that card is also what stops a
   * moderator's page polling a route that would refuse it.
   */
  const mayRun = useMayRunShows();
  const [detail, setDetail] = useState<ShowDetail | undefined>(undefined);
  /**
   * **Two error channels, because they have different lifetimes.**
   *
   * `loadError` belongs to reading the show and is cleared by the next successful read — it is a
   * statement about whether the page in front of you is current.
   *
   * `actionError` belongs to something you pressed, and must survive the reload that follows.
   * Every action re-reads the show when it finishes, including when it failed, so with one shared
   * error the reload's success wiped the failure a few milliseconds after it appeared: the
   * message flashed and vanished, and the only thing the operator learned was that *something*
   * had gone wrong. It is cleared when the next action starts, and not before.
   */
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [problems, setProblems] = useState<LotProblem[] | undefined>(undefined);
  const [busy, setBusy] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<string | undefined>(undefined);
  /**
   * Held here rather than inside `ShowDetails`, because the button that opens the form sits with
   * the other show actions — see the note in that file.
   */
  const [editing, setEditing] = useState(false);
  /**
   * The server's half of the pre-flight check, reported upwards by `Broadcast` (`R-HOST-11`).
   * `undefined` means it has not been read yet — or that there is no broadcast panel on screen,
   * which is the case for a draft show, where *go live* is not offered at all.
   */
  const [preflight, setPreflight] = useState<Preflight | undefined>(undefined);
  /**
   * Whether this browser is the camera (`monitor/Monitor.tsx`).
   *
   * Reported by `Broadcast`, which is the only thing that knows. False for a moderator, who
   * never renders that card at all — and correctly so: they are exactly who the monitor is for.
   */
  const [publishingHere, setPublishingHere] = useState(false);
  /** Presenter mode (`presenter/PresenterOverlay.tsx`). Only ever on while the show is live. */
  const [presenting, setPresenting] = useState(false);
  /*
    The room's comments, held here rather than in `ChatPanel` so presenter mode can draw the same
    feed without joining the room a second time. Connects only while the show is live, as it did.
  */
  const chat = useRoomChat(detail?.show.publicId ?? "", detail?.show.state === "live");

  /** One read's outcome. See the same shape in `ShowsPage` for why the effect uses `.then`. */
  const apply = useCallback(
    (next: ShowDetail | undefined, failure: unknown) => {
      if (next) setDetail(next);
      setLoadError(
        failure === undefined
          ? undefined
          : failure instanceof ApiError
            ? failure.message
            : t("shows.unreachable"),
      );
    },
    [t],
  );

  /**
   * **Never rejects**, and callers depend on that. `act` awaits it in a `finally`, and the lot
   * components await it after their own error handling, so a rejection here would surface as the
   * action having failed when only the refresh did. Keep the `catch`.
   */
  const load = useCallback(async () => {
    if (!showId) return;
    try {
      apply(await shows.read(showId), undefined);
    } catch (failure) {
      apply(undefined, failure);
    }
  }, [showId, apply]);

  useEffect(() => {
    if (!showId) return;
    let live = true;
    void shows.read(showId).then(
      (result) => {
        if (live) apply(result, undefined);
      },
      (failure: unknown) => {
        if (live) apply(undefined, failure);
      },
    );
    return () => {
      live = false;
    };
  }, [showId, apply]);

  /**
   * **And again, on a timer** (`R-HOST-14`, `R-HOST-31`, §17.4 — see `panelPollInterval`).
   *
   * The read above happens once. Everything the host is watching while a show runs — units left,
   * lots sold out, each lot's own figures, the allocation ledger — changes because a *customer*
   * did something, so a page that only re-reads after its own actions shows the numbers as they
   * were when it was opened, and pressing something unrelated becomes the only way to see a sale.
   *
   * It is safe against what the host is typing, and that is a property of the components rather
   * than of this timer: `LotRow` and `ShowDetails` seed their inputs with `useState` on mount and
   * never sync from props, and the rows are keyed by lot id, so a re-render replaces figures and
   * leaves half-typed prices and allocations alone. **Keep it that way** — a `useEffect` in
   * either that copied props into state would make this poll wipe the field mid-word.
   *
   * `load` never rejects and touches only the show and the load error, so a failed poll shows
   * *this page may be out of date* and clears itself on the next success, leaving an action's own
   * error and a refused publication's problem list where they are.
   */
  const pollMs = panelPollInterval(detail?.show.state);

  useEffect(() => {
    if (!showId || pollMs === undefined) return;
    const timer = setInterval(() => void load(), pollMs);
    return () => clearInterval(timer);
  }, [showId, load, pollMs]);

  async function act(action: "publish" | "live" | "end" | "cancel") {
    if (!showId) return;
    setBusy(action);
    setActionError(undefined);
    setProblems(undefined);
    setNote(undefined);
    try {
      const result = await shows.act(showId, action);
      // `R-HOST-27` — unsold allocation returning to the storefront is reported as confirmed,
      // because the host's next question after ending a show is whether the stock is back on sale.
      if (action === "end") {
        setNote(
          t("shows.endedNote", {
            /*
              M1.13 — nothing moves at the end. What the host is told is how many sold units are
              still held at Shopify, which settlement gives back order by order as each is created.
            */
            held: result.inventory?.held ?? 0,
            /**
             * `R-SYNC-12` — how many customer orders are on their way to Shopify. Their progress
             * is the settlement table's below, which polls; ending the show no longer waits for
             * any of them, so there is no count of *sent* to report here.
             */
            closed: result.orders?.closed ?? 0,
          }) +
            /*
              `R-GIVE-5` — said at the moment the show ends, because one winner for five units is
              the rule working and reads like a fault unless somebody is told why.
            */
            (result.giveaways ?? [])
              .map((draw) =>
                draw.winners === 0
                  ? ` ${t("giveaway.endedNoBuyers")}`
                  : draw.winners < draw.quantity
                    ? ` ${t("giveaway.endedShort", { quantity: draw.quantity, eligible: draw.eligible })}`
                    : ` ${t("giveaway.endedNote", { count: draw.winners })}`,
              )
              .join(""),
        );
      }
      if (action === "publish") {
        // M1.13 — publishing takes nothing from the storefront, so there is nothing to count.
        setNote(t("shows.publishedNote"));
      }
    } catch (failure) {
      if (failure instanceof ApiError && Array.isArray(failure.body.problems)) {
        // `R-HOST-9` — publication is blocked and the panel says which lots and why, rather than
        // reporting that the show is invalid and leaving the host to find out how.
        setProblems(failure.body.problems as LotProblem[]);
      } else {
        setActionError(
          failure instanceof ApiError ? failure.message : t("shows.unreachable"),
        );
      }
    } finally {
      setBusy(undefined);
      // Reloaded even when the action failed: the commonest failure is somebody else having taken
      // the same action a moment ago, and the refreshed show is the explanation.
      await load();
    }
  }

  if (!detail) {
    return loadError ? (
      <Alert color="red" variant="light" radius="md">
        {loadError}
      </Alert>
    ) : (
      <Group justify="center" py="xl">
        <Loader size="sm" color="ink" />
      </Group>
    );
  }

  const { show, lots, summary } = detail;
  const link = shareLink(show.publicId);
  const building = show.state === "draft";
  const live = show.state === "live";
  /*
    Presenter mode, drawn over whichever picture this page has: the camera preview when this
    browser is broadcasting, the stream monitor when another device is. A show that stops being
    live takes it down with it — there is no room left to present to.
  */
  const presenter =
    presenting && live ? (
      <PresenterOverlay
        publicId={show.publicId}
        chat={chat}
        sales={detail.recentSales}
        onExit={() => setPresenting(false)}
      />
    ) : undefined;
  const onAir = lots.filter((lot) => ["featured", "selling"].includes(lot.state));
  const shortlist = lots.filter((lot) => lot.state === "queued");
  const done = lots.filter(
    (lot) => !["featured", "selling", "queued"].includes(lot.state),
  );

  return (
    <Stack gap="lg">
      <Anchor component={Link} to="/" size="xs">
        {t("shows.backToList")}
      </Anchor>

      <Paper withBorder radius="lg" p="lg" className={styles.card}>
        <Stack gap="md">
          {/* Wraps rather than `nowrap`: the broadcasting device is a phone
              (`docs/runbooks/video.md` §2), and on that width the title and the action buttons cannot
              share a line. On a laptop there is room and nothing changes. */}
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap={4}>
              <Group gap="sm">
                <Text fw={650} size="lg">
                  {show.title}
                </Text>
                <ShowStateBadge state={show.state} />
              </Group>
              <ShowDetails
                /*
                 * Remounted when the form opens, which seeds its inputs from the show as it
                 * stands right now. Without the key the fields would still hold whatever they
                 * were seeded with on first render — stale the moment anybody else edits the
                 * show, which on a panel two people are using is not a corner case.
                 */
                key={editing ? "editing" : "idle"}
                show={show}
                editing={editing}
                onChanged={load}
                onClose={() => setEditing(false)}
              />
            </Stack>
            {/* Running the show is the host's (spec §3). A moderator sees the show and its
                comments; publishing it, starting it and ending it are not theirs, and the
                routes behind these buttons answer 403. */}
            <Group gap="xs" align="flex-start" wrap="wrap">
              {mayRun ? (
                <ShowActions
                  state={show.state}
                  busy={busy}
                  editing={editing}
                  preflight={preflight}
                  publishingHere={publishingHere}
                  onEdit={() => setEditing(true)}
                  onAct={act}
                />
              ) : null}
            </Group>
          </Group>

          {/* `R-HOST-33` — sharing is a first-class action with a copy button, not a URL the host
              reconstructs from the address bar. It is shown on a draft show too, because
              `R-HOST-35` is precisely that the link can be prepared before the show is visible. */}
          <Group gap="xs" wrap="nowrap">
            <Text size="xs" className={styles.link}>
              {link}
            </Text>
            <CopyButton value={link}>
              {({ copied, copy }) => (
                <Button size="compact-xs" variant="default" onClick={copy}>
                  {copied ? t("shows.copied") : t("shows.copy")}
                </Button>
              )}
            </CopyButton>
            {building ? (
              <Badge size="xs" variant="light" color="gray">
                {t("shows.linkNotLive")}
              </Badge>
            ) : null}
          </Group>

          {/* `R-HOST-37` — the picture the link previews with, beside the link it belongs to. */}
          <ShowThumbnail
            show={show}
            mayEdit={mayRun && ["draft", "scheduled", "live"].includes(show.state)}
            onChanged={load}
          />

          {/* `R-SHOW-26a` — beside the link, because what it changes is where `/live` points. */}
          <ShowTestRun show={show} mayEdit={mayRun} onChanged={load} />

          {/* `R-END-6a` — once the show is over, where its link sends people instead. */}
          {["ended", "cancelled"].includes(show.state) ? (
            <ShowRedirect show={show} mayEdit={mayRun} onChanged={load} />
          ) : null}

          {/*
            Only `pending`. A `failed` move means Shopify was reached and refused, so nothing
            moved and there is nothing to reconcile — and because the ledger keeps it forever,
            alarming on it made a show that had ever been refused once warn on every page load
            with no way to clear it. `pending` is the state nobody knows the answer to, and it is
            the one worth interrupting somebody for.
          */}
          {summary.inventory.pending > 0 ? (
            <Alert color="orange" variant="light" radius="md">
              {t("shows.inventoryUnresolved", { pending: summary.inventory.pending })}
            </Alert>
          ) : null}

          {note ? (
            <Alert color="green" variant="light" radius="md">
              {note}
            </Alert>
          ) : null}
          {/* The action's failure, which outlives the reload that follows it. */}
          {actionError ? (
            <Alert
              color="red"
              variant="light"
              radius="md"
              withCloseButton
              onClose={() => setActionError(undefined)}
            >
              {actionError}
            </Alert>
          ) : null}
          {/* And a separate one for "this page may be out of date", which is a different problem
              with a different remedy and clears itself the moment a read succeeds. */}
          {loadError ? (
            <Alert color="orange" variant="light" radius="md">
              {loadError}
            </Alert>
          ) : null}
          {problems ? (
            <Alert
              color="red"
              variant="light"
              radius="md"
              title={t("shows.cannotPublish")}
            >
              <List size="sm" spacing={4}>
                {problems.map((problem) => (
                  <List.Item key={`${problem.lotId}-${problem.code}`}>
                    {t("shows.problem", {
                      position: problem.position + 1,
                      message: problem.message,
                    })}
                  </List.Item>
                ))}
              </List>
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      {/*
        The figures, in a bar of their own that **stays at the top of the window** while the host
        scrolls down to the running order — which is where they spend the show, and where *how
        many are watching* and *what is left* are still the numbers they are asked. Outside the
        card above, because a sticky element only sticks within its parent: inside the card it
        would scroll away with it.
      */}
      <Paper
        withBorder
        radius="lg"
        px="lg"
        py="sm"
        className={`${styles.card} ${styles.stats}`}
      >
        {/*
          §17.4's *what the host must be able to see at a glance*, in its own order of urgency:
          who is watching, what the show has taken, then what is left to sell. **One line that
          scrolls sideways** rather than wrapping: on the phone a host broadcasts from, a wrapped
          row is three lines pinned over the page, and the figures that matter most come first.
        */}
        <Group gap="xl" wrap="nowrap" className={styles.statsRow}>
          {/*
              `R-ANL-1` — the same count the room shows its viewers, from the same Redis key, so
              the host and the audience are reading one number.

              Only while there is a room to be in. A draft has nobody watching by definition and
              a finished show has had its presence dropped, and *0 watching* on either is a figure
              that reads as a fault rather than as an absence.
            */}
          {["scheduled", "live"].includes(show.state) ? (
            <Figure label={t("shows.watchingLabel")} value={String(summary.viewers)} />
          ) : null}
          {/*
              §17.4's *revenue so far*, and it is **two** figures rather than one because
              `R-ANL-5` forbids the headline: *a show's takings and its shipping fees are
              different things and must not be summed.* Goods first, since that is the number a
              host is asked for; delivery beside it, because a show that has taken £40 of which
              £12 is postage is a different show from one that has taken £40 of goods.
            */}
          <Figure
            label={t("shows.revenueLabel")}
            value={formatPrice(summary.takings.goods)}
          />
          {/*
            The same goods revenue, split by how it sold: won at auction and claimed at a fixed
            price. Beside the total rather than instead of it, because the total is the number a host
            is asked for. The two always add up to it, and delivery is in neither (`R-ANL-5`).
          */}
          <Figure
            label={t("shows.auctionRevenueLabel")}
            value={formatPrice(summary.takings.auction)}
          />
          <Figure
            label={t("shows.fixedRevenueLabel")}
            value={formatPrice(summary.takings.fixed)}
          />
          <Figure
            label={t("shows.deliveryLabel")}
            value={formatPrice(summary.takings.shipping)}
          />
          <Figure label={t("shows.lotsLabel")} value={String(summary.lots)} />
          {/*
              §17.4's *lots sold against lots remaining*, in units — the figure a host is asked out
              loud. The count of lots that sold **out** is no longer shown (M1.13): with no running
              order built in advance it answered nothing anybody was asking.
            */}
          <Figure label={t("shows.unitsSoldLabel")} value={String(summary.unitsSold)} />
          <Figure label={t("shows.remainingLabel")} value={String(summary.remaining)} />
          {/* `R-INV-8` as amended by M1.13 — sold units held at Shopify against the sales still
                waiting for an order. The two trail each other by seconds while a show sells, and
                meet at zero once every order is created; a gap that stays is a hold that failed,
                which the stock-hold alarm reports (`docs/runbooks/catalogue.md` §4). */}
          <Figure
            label={t("shows.inventoryLabel")}
            value={t("shows.inventoryValue", {
              held: summary.inventory.held,
              owed: summary.inventory.owed,
            })}
            warn={summary.inventory.pending > 0}
          />
        </Group>
      </Paper>

      {/*
        Broadcasting is offered from `scheduled` onwards and never on a draft: a draft holds no
        allocation, so a stage created for one is a quota consumed by a show that cannot sell
        anything. The API refuses it, so offering the panel here would be offering a button that
        answers 409.

        A host who wants to check the camera before anybody is watching publishes the show and
        does not send the link — which is what replaced `R-HOST-12`'s rehearsal mode when it was
        struck (spec §17.3).
      */}
      {mayRun && ["scheduled", "live"].includes(show.state) ? (
        <Broadcast
          showId={show.id}
          live={show.state === "live"}
          onPreflight={setPreflight}
          onPublishing={setPublishingHere}
          presenting={presenter !== undefined && publishingHere}
          overlay={presenter}
          onPresent={() => setPresenting(true)}
        />
      ) : null}

      {/*
        What the room is seeing (`R-VID-9`, `R-HOST-19`), for everybody who is not the camera.

        A live show only — there is no stage to subscribe to before one and nothing on it after.
        Withheld while this browser is publishing, because the presenter already has the same
        picture as a camera preview and a second copy of it beside their own microphone is a
        feedback loop with a delay. A moderator never renders `Broadcast`, so `publishingHere`
        is false for them and this is the only video on their page, which is the point of it.
      */}
      {show.state === "live" && !publishingHere ? (
        <Monitor
          publicId={show.publicId}
          live={show.state === "live"}
          turns={show.videoRotation}
          presenting={presenter !== undefined}
          overlay={presenter}
          /*
            Presenter mode from a browser that is not the camera — a host presenting from a second
            device, or a moderator reading comments aloud. It changes nothing on the show.
          */
          onPresent={() => setPresenting(true)}
        />
      ) : null}

      {/*
        Comments (M0.10). Offered on a live show, and on an ended one for the mute list alone —
        which after the show is the record of what was done (`R-MOD-2`) rather than a set of
        controls. Never on a draft or a scheduled show: there is no room before a show starts
        (spec §10.1.1), so there would be nothing to connect to and nothing to moderate.
      */}
      {["live", "ended"].includes(show.state) ? (
        <ChatPanel showId={show.id} live={show.state === "live"} chat={chat} />
      ) : null}

      {/*
        Settlement (M0.12). Only on an ended show, because before it ends there is nothing to be
        wrong with — see the note in `Settlement.tsx`. `R-SYNC-16` is why it is on this page at
        all rather than only in CloudWatch: an order that never became a Shopify order is an
        incident, and the person who ran the show is the one who notices soonest.
      */}
      {mayRun && show.state === "ended" ? <Settlement showId={show.id} /> : null}

      {/*
        The giveaway (spec §15.10). Set up before or during the show, drawn when it ends, and
        after that the record of who won. Not on a cancelled show — nothing is drawn for one.
        Keyed by the state so that ending the show remounts it and it reads the draw's result
        rather than holding the pending giveaway it loaded before.
      */}
      {show.state !== "cancelled" ? (
        <GiveawayCard
          key={show.state}
          showId={show.id}
          showState={show.state}
          controls={mayRun}
        />
      ) : null}

      {/*
        M1.13 — the catalogue, searched. During the show this is the operator's console: pick a
        product and feature it, with its stock checked as it is. Before the show it builds the
        shortlist, which holds no stock and is optional — anything in the catalogue can be featured.
      */}
      {mayRun && ["draft", "scheduled", "live"].includes(show.state) ? (
        <ProductPicker
          showId={show.id}
          mode={live ? "feature" : "shortlist"}
          onChanged={load}
        />
      ) : null}

      {mayRun && live ? (
        <Shortcuts showId={show.id} onAir={onAir} onChanged={load} />
      ) : null}

      {lots.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t(live ? "shows.nothingFeatured" : "shows.noLots")}
        </Text>
      ) : live ? (
        <>
          <LotSection
            title={t("shows.onAir")}
            lots={onAir}
            empty={t("shows.nothingOnAir")}
            show={detail}
            mayRun={mayRun}
            onChanged={load}
          />
          <LotSection
            title={t("shows.shortlist")}
            lots={shortlist}
            show={detail}
            mayRun={mayRun}
            onChanged={load}
          />
          <LotSection
            title={t("shows.doneLots")}
            lots={done}
            show={detail}
            mayRun={mayRun}
            onChanged={load}
          />
        </>
      ) : (
        <LotSection
          title={t("shows.shortlist")}
          lots={lots}
          show={detail}
          mayRun={mayRun}
          onChanged={load}
        />
      )}
    </Stack>
  );
}

/**
 * A titled group of lots. On a live show the page has three — on air, the shortlist, and what is
 * done — because an operator's eye goes to the first and hardly ever to the last.
 */
function LotSection({
  title,
  lots,
  empty,
  show,
  mayRun,
  onChanged,
}: {
  title: string;
  lots: Lot[];
  /** Said when the group is empty; an empty group with no sentence is left out altogether. */
  empty?: string;
  show: ShowDetail;
  mayRun: boolean;
  onChanged: () => Promise<void>;
}) {
  if (lots.length === 0 && !empty) return null;
  return (
    <Stack gap="xs">
      <Text size="xs" fw={700} tt="uppercase" c="dimmed">
        {title}
      </Text>
      {lots.length === 0 ? (
        <Text size="sm" c="dimmed">
          {empty}
        </Text>
      ) : (
        lots.map((lot) => (
          <LotRow
            key={lot.id}
            showId={show.show.id}
            showState={show.show.state}
            lot={lot}
            /* Read-only for a moderator: knowing what is selling is half of reading a room, and
               driving it is the host's (spec §3). */
            controls={mayRun}
            /* `R-HOST-15` — one lot in front of the room at a time. */
            anotherOpen={show.lots.some(
              (other) => other.id !== lot.id && other.state === "selling",
            )}
            onChanged={onChanged}
          />
        ))
      )}
    </Stack>
  );
}

/**
 * `R-HOST-15`'s keyboard shortcuts for the lot on air: **O** opens it, **C** closes it. Ignored
 * while the operator is typing — in the search box above all — so a product called *Cocoa* can
 * be searched for without closing anything. `/` puts the cursor in the search, which is the
 * catalogue search's own shortcut.
 */
function Shortcuts({
  showId,
  onAir,
  onChanged,
}: {
  showId: string;
  onAir: Lot[];
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(target.tagName))
        return;
      if (target?.isContentEditable) return;
      const key = event.key.toLowerCase();
      const featured = onAir.find((lot) => lot.state === "featured");
      const current = onAir[0];
      if (key === "o" && featured) {
        event.preventDefault();
        void shows
          .actOnLot(showId, featured.id, "open")
          .finally(() => void onChanged());
      } else if (key === "c" && current) {
        event.preventDefault();
        void shows
          .actOnLot(showId, current.id, "close")
          .finally(() => void onChanged());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showId, onAir, onChanged]);

  return (
    <Text size="xs" c="dimmed">
      {t("shows.shortcuts")}
    </Text>
  );
}

function Figure({
  label,
  value,
  warn,
}: {
  label: string;
  value: string;
  warn?: boolean;
}) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={600} c={warn ? "red" : undefined}>
        {value}
      </Text>
    </Stack>
  );
}

/**
 * The four transitions, offered only from the state each is legal from (spec §8.1).
 *
 * **Ending asks first** (`R-HOST-25`): a show never ends by itself and only the host or an
 * operator ends one, so the panel makes it deliberate rather than a single stray click. It is the
 * one irreversible action on this page. Cancelling asks too, for the same reason.
 *
 * ## A live show offers exactly one action
 *
 * **End show, and nothing else.** Not Edit — a title or a start time is not what anybody is
 * looking at once the show is running, and the start time in particular has stopped meaning
 * anything the moment the show started. And not Cancel: spec §8's diagram permits
 * `live → cancelled`, but cancelling says *this will not take place* and a show being broadcast
 * plainly is. `R-SHOW-6` already refuses it the moment anything sells, so the button was only
 * ever usable in the narrow window before the first sale — a window in which "end it" is the
 * right answer anyway.
 *
 * The route still accepts it (`show-action.ts`), because `R-SHOW-4` is about an operator always
 * having a way through and the panel is not the only caller. What is removed is the invitation.
 */
function ShowActions({
  state,
  busy,
  editing,
  preflight,
  publishingHere,
  onEdit,
  onAct,
}: {
  state: ShowDetail["show"]["state"];
  busy: string | undefined;
  /** True while the details form is open, which is when its own Save and Discard take over. */
  editing: boolean;
  /** The pre-flight check, or `undefined` before it has been read (`R-HOST-11`). */
  preflight: Preflight | undefined;
  /** Whether this browser is the camera — reported by `Broadcast`. See `goLiveHere` below. */
  publishingHere: boolean;
  onEdit: () => void;
  onAct: (action: "publish" | "live" | "end" | "cancel") => Promise<void>;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState<"end" | "cancel" | undefined>(undefined);

  if (["ended", "cancelled"].includes(state)) return null;

  const live = state === "live";

  /**
   * **Going live is offered only on the device that is broadcasting.**
   *
   * Pressed on a second device — the laptop driving the running order while a phone is the
   * camera — it broke the phone's broadcast on the night it was tried, and the cause has not been
   * pinned down: the server's `live` transition touches nothing on the stage, so it is something
   * the pressing browser does next. Until it is, the button is where it is prevented. The camera
   * is the one device certain to be in front of the host at the moment a show starts, so asking
   * for the press there costs nothing.
   *
   * The panel's rule rather than the route's: which device is pressing is not something the
   * server can know. `POST .../live` still requires *somebody* to be publishing (`R-HOST-11`).
   */
  const goLiveHere = preflight?.ready === true && publishingHere;

  if (confirming) {
    return (
      /*
        The question is held to 60% of the row and wraps inside it, and the two answers never
        shrink. On a phone the sentence otherwise took the room the buttons needed, and Mantine's
        default (`preventGrowOverflow`) cut *Yes* and *No* down to fit.
      */
      <Group gap="xs" wrap="nowrap" preventGrowOverflow={false}>
        <Text size="xs" c="dimmed" maw="60%">
          {t(confirming === "end" ? "shows.confirmEnd" : "shows.confirmCancel")}
        </Text>
        <Button
          size="compact-sm"
          color="red"
          flex="none"
          loading={busy === confirming}
          onClick={() => void onAct(confirming).then(() => setConfirming(undefined))}
        >
          {t("shows.confirmYes")}
        </Button>
        <Button
          size="compact-sm"
          variant="default"
          flex="none"
          onClick={() => setConfirming(undefined)}
        >
          {t("shows.confirmNo")}
        </Button>
      </Group>
    );
  }

  return (
    <Group gap="xs" wrap="nowrap">
      {state === "draft" ? (
        <Button
          size="compact-sm"
          loading={busy === "publish"}
          onClick={() => void onAct("publish")}
        >
          {t("shows.publish")}
        </Button>
      ) : null}
      {/*
        `R-HOST-11` — disabled until the pre-flight check passes, with the reason in its tooltip.
        Disabling it is the courtesy; `POST .../live` refuses the transition regardless, which is
        what makes going live broken impossible rather than merely discouraged.
      */}
      {state === "scheduled" ? (
        <Tooltip
          label={
            goLiveHere
              ? t("shows.goLiveReady")
              : preflight?.ready
                ? t("shows.goLiveElsewhere")
                : preflight
                  ? t("shows.goLiveBlocked")
                  : t("shows.goLiveChecking")
          }
          withArrow
        >
          {/*
            `data-disabled` rather than `disabled`: a genuinely disabled element receives no
            pointer events, so a tooltip on one never appears — and the tooltip is the whole point
            here, because a button that is greyed out with no explanation is worse than one that
            fails with a message. Mantine styles this identically and the click is refused below.
          */}
          <Button
            size="compact-sm"
            loading={busy === "live"}
            data-disabled={goLiveHere ? undefined : true}
            onClick={() => {
              if (goLiveHere) void onAct("live");
            }}
          >
            {t("shows.goLive")}
          </Button>
        </Tooltip>
      ) : null}
      {state === "live" ? (
        <Button size="compact-sm" color="red" onClick={() => setConfirming("end")}>
          {t("shows.end")}
        </Button>
      ) : null}
      {/* Beside cancelling rather than in the date line, so that the things a person can do to a
          show read as one set. Hidden while the form is open — it has its own Save and Discard —
          and hidden on a live show, where there is one thing left to do and it is not this. */}
      {editing || live ? null : (
        <Button size="compact-sm" variant="default" onClick={onEdit}>
          {t("shows.editDetails")}
        </Button>
      )}
      {live ? null : (
        <Button
          size="compact-sm"
          variant="default"
          onClick={() => setConfirming("cancel")}
        >
          {t("shows.cancel")}
        </Button>
      )}
    </Group>
  );
}
